import React, { useState } from 'react';
import { usePolling } from '@/hooks/usePolling';
import { useCache } from '@/hooks/useCache';
import { toast } from '@/utils/toast';
import { debug } from '@/utils/debug';
import { GoogleAdsLinker } from '@/components/features/GoogleAdsLinker';
import { PollingStatus } from '@/components/features/PollingStatus';
import { CacheIndicator } from '@/components/debug/CacheIndicator';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import styles from './TestWeek3.module.css';

export function TestWeek3() {
  const [showGoogleAdsLinker, setShowGoogleAdsLinker] = useState(false);
  const [pollingCounter, setPollingCounter] = useState(0);
  
  // Test usePolling hook
  const mockPollFunction = async (customerId: string) => {
    setPollingCounter(prev => prev + 1);
    debug.log('Mock poll function called', { customerId, counter: pollingCounter });
    // Simulate success after 5 attempts
    return pollingCounter >= 4;
  };
  
  const {
    state: pollingState,
    startPolling,
    stopPolling
  } = usePolling(mockPollFunction, {
    onSuccess: () => {
      toast.success('Polling completed successfully!');
      debug.log('Polling success');
    },
    onTimeout: (customerId) => {
      toast.error('Polling timed out');
      debug.warn('Polling timeout', { customerId });
    },
    onError: (error) => {
      toast.error(`Polling error: ${error}`);
      debug.error('Polling error', error);
    },
    customerId: 'test-customer-123'
  });
  
  // Test useCache hook
  const mockDataFetcher = async () => {
    debug.log('Fetching mock data...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      timestamp: Date.now(),
      data: `Mock data fetched at ${new Date().toLocaleTimeString()}`
    };
  };
  
  const {
    data: cachedData,
    isLoading: cacheLoading,
    cacheAge,
    isCached,
    refresh: refreshCache
  } = useCache('test-data', mockDataFetcher, {
    ttl: 30000 // 30 seconds
  });
  
  // Test notifications
  const testNotifications = () => {
    toast.success('Success notification!');
    setTimeout(() => toast.info('Info notification!'), 500);
    setTimeout(() => toast.warning('Warning notification!'), 1000);
    setTimeout(() => toast.error('Error notification!'), 1500);
  };
  
  // Test promise toast
  const testPromiseToast = () => {
    const mockPromise = new Promise<string>((resolve) => {
      setTimeout(() => resolve('Operation completed!'), 2000);
    });
    
    toast.promise(mockPromise, {
      loading: 'Processing...',
      success: 'Successfully completed!',
      error: 'Operation failed!'
    });
  };
  
  // Test debug features
  const triggerDebugActions = () => {
    // These will be recorded in the debug store
    if (import.meta.env.DEV && window.__DEBUG_STORE__) {
      window.__DEBUG_STORE__.addApiLog({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        method: 'GET',
        url: '/api/test',
        status: 200,
        duration: 123,
        request: null,
        response: { test: 'data' }
      });
      
      window.__DEBUG_STORE__.addOptimizationRun({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        params: { target_density: 0.7, accounts_budget: 1000, campaign_ids: ['test-1'], trigger_source: 'test' },
        result: { optimized_campaigns: [], total_budget: 1000, processing_time: 100, algorithm: 'test', convergence: true, iterations: 1 },
        duration: 456,
        triggerSource: 'test'
      });
      
      window.__DEBUG_STORE__.recordPerformance('test-metric', Math.random() * 100);
    }
    
    toast.info('Debug actions triggered! Press Ctrl+Shift+D to see debug panel.');
  };
  
  return (
    <div className={styles.container}>
      <h1>Week 3 Feature Test Page</h1>
      
      {/* Polling Test */}
      <Card className={styles.section}>
        <h2>🔄 Polling Hook Test</h2>
        <p>Test the adaptive polling system with fast/slow phases.</p>
        
        <div className={styles.controls}>
          <Button onClick={() => startPolling('test-customer-123')} disabled={pollingState.isPolling}>
            Start Polling
          </Button>
          <Button onClick={stopPolling} disabled={!pollingState.isPolling}>
            Stop Polling
          </Button>
          <Button onClick={() => {
            stopPolling();
            setPollingCounter(0);
          }}>
            Reset
          </Button>
        </div>
        
        {pollingState.isPolling && (
          <PollingStatus
            attempts={pollingState.totalAttempts}
            phase={pollingState.phase}
          />
        )}
        
        <p className={styles.info}>
          Counter: {pollingCounter} (will succeed at 5)
        </p>
      </Card>
      
      {/* Cache Test */}
      <Card className={styles.section}>
        <h2>💾 Cache Hook Test</h2>
        <p>Test the cache management system with TTL and refresh.</p>
        
        <div className={styles.cacheHeader}>
          <h3>Cached Data</h3>
          {debug.dev(
            <CacheIndicator cacheAge={cacheAge} onRefresh={refreshCache} />
          )}
        </div>
        
        {cacheLoading ? (
          <p>Loading...</p>
        ) : (
          <div className={styles.cacheData}>
            <p>Cached: {isCached ? 'Yes' : 'No'}</p>
            <p>Data: {cachedData?.data || 'No data'}</p>
          </div>
        )}
        
        <Button onClick={refreshCache}>Force Refresh</Button>
      </Card>
      
      {/* Google Ads Linker Test */}
      <Card className={styles.section}>
        <h2>🔗 Google Ads Linker Test</h2>
        <p>Test the Google Ads linking flow with modal and polling.</p>
        
        <Button onClick={() => setShowGoogleAdsLinker(true)}>
          Open Google Ads Linker
        </Button>
        
        <GoogleAdsLinker
          isOpen={showGoogleAdsLinker}
          onClose={() => setShowGoogleAdsLinker(false)}
          onConnect={(customerId) => {
            debug.log('TestWeek3: Google Ads link requested', { customerId });
            toast.success(`Linking account: ${customerId}`);
          }}
        />
      </Card>
      
      {/* Notification Test */}
      <Card className={styles.section}>
        <h2>🔔 Notification System Test</h2>
        <p>Test the toast notification system.</p>
        
        <div className={styles.controls}>
          <Button onClick={testNotifications}>
            Show All Types
          </Button>
          <Button onClick={testPromiseToast}>
            Promise Toast
          </Button>
          <Button onClick={() => toast.success('Custom toast!', { duration: 10000 })}>
            Long Toast (10s)
          </Button>
        </div>
      </Card>
      
      {/* Debug Features Test */}
      <Card className={styles.section}>
        <h2>🐛 Debug Features Test</h2>
        <p>Test debug components and utilities (DEV only).</p>
        
        <div className={styles.controls}>
          <Button onClick={triggerDebugActions}>
            Trigger Debug Actions
          </Button>
          <Button onClick={() => debug.log('Test log message')}>
            Console Log
          </Button>
          <Button onClick={() => {
            debug.time('test-timer');
            setTimeout(() => debug.timeEnd('test-timer'), 1000);
          }}>
            Test Timer (1s)
          </Button>
        </div>
        
        <p className={styles.hint}>
          Press <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>D</kbd> to toggle debug panel
        </p>
      </Card>
    </div>
  );
}